import React from "react";

export const formatNumber = (num: number): string => {
	if (!num && num !== 0) return "0";
	num = Math.round(num);
	if (num >= 1_000_000_000) {
		return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B"; // Billion
	}
	if (num >= 1_000_000) {
		return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"; // Million
	}
	if (num >= 10_000) {
		return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K"; // Thousand
	}
	return new Intl.NumberFormat("en-US").format(num);
};

/**
 * Formats numbers 1,000 and above with a comma separator.
 * Safe to pass numbers, strings, or null/undefined.
 */
export const formatNumberCommas = (value: number | string | null | undefined): string => {
	// 1. Failsafe for empty or invalid values
	if (value === null || value === undefined || value === "") {
		return "0";
	}

	// 2. Ensure we are working with a valid number
	const num = Number(value);
	if (Number.isNaN(num)) {
		return "0";
	}

	// 3. Format with native comma separation
	return new Intl.NumberFormat("en-US").format(num);
};

// Country data with short names and flags
export interface CountryOption {
	label: string;
	value: string;
	short: string;
	flag: string;
}

export const countryOptionsMap = new Map<string, CountryOption>([
	["Argentina", { label: "Argentina", value: "Argentina", short: "AR", flag: "🇦🇷" }],
	["Australia", { label: "Australia", value: "Australia", short: "AU", flag: "🇦🇺" }],
	["Belgium", { label: "Belgium", value: "Belgium", short: "BE", flag: "🇧🇪" }],
	["Brazil", { label: "Brazil", value: "Brazil", short: "BR", flag: "🇧🇷" }],
	["Canada", { label: "Canada", value: "Canada", short: "CA", flag: "🇨🇦" }],
	["Cambodia", { label: "Cambodia", value: "Cambodia", short: "CBDA", flag: "🇰🇭" }],
	["Côte d'Ivoire", { label: "Côte d'Ivoire (Ivory Coast)", value: "Côte d'Ivoire", short: "CI", flag: "🇨🇮" }],
	["China", { label: "China", value: "China", short: "CN", flag: "🇨🇳" }],
	["Germany", { label: "Germany", value: "Germany", short: "DE", flag: "🇩🇪" }],
	["Denmark", { label: "Denmark", value: "Denmark", short: "DK", flag: "🇩🇰" }],
	["Egypt", { label: "Egypt", value: "Egypt", short: "EG", flag: "🇪🇬" }],
	["Fiji", { label: "Fiji", value: "Fiji", short: "FJ", flag: "🇫🇯" }],
	["France", { label: "France", value: "France", short: "FR", flag: "🇫🇷" }],
	["GCC", { label: "Gulf Cooperation Council", value: "GCC", short: "GCC", flag: "🏳️" }],
	["Ghana", { label: "Ghana", value: "Ghana", short: "GH", flag: "🇬🇭" }],
	["Hong Kong", { label: "Hong Kong", value: "Hong Kong", short: "HK", flag: "🇭🇰" }],
	["Hungary", { label: "Hungary", value: "Hungary", short: "HU", flag: "🇭🇺" }],
	["Indonesia", { label: "Indonesia", value: "Indonesia", short: "ID", flag: "🇮🇩" }],
	["India", { label: "India", value: "India", short: "IN", flag: "🇮🇳" }],
	["Iraq", { label: "Iraq", value: "Iraq", short: "IQ", flag: "🇮🇶" }],
	["Italy", { label: "Italy", value: "Italy", short: "IT", flag: "🇮🇹" }],
	["Jordan", { label: "Jordan", value: "Jordan", short: "JO", flag: "🇯🇴" }],
	["Japan", { label: "Japan", value: "Japan", short: "JP", flag: "🇯🇵" }],
	["Kazakhstan", { label: "Kazakhstan", value: "Kazakhstan", short: "KAZ", flag: "🇰🇿" }],
	["Kenya", { label: "Kenya", value: "Kenya", short: "KE", flag: "🇰🇪" }],
	["South Korea", { label: "South Korea", value: "South Korea", short: "KR", flag: "🇰🇷" }],
	["Saudi Arabia", { label: "Kingdom of Saudi Arabia", value: "Saudi Arabia", short: "KSA", flag: "🇸🇦" }],
	["Libya", { label: "Libya", value: "Libya", short: "LY", flag: "🇱🇾" }],
	["Morocco", { label: "Morocco", value: "Morocco", short: "MA", flag: "🇲🇦" }],
	["Mongolia", { label: "Mongolia", value: "Mongolia", short: "MN", flag: "🇲🇳" }],
	["Macao", { label: "Macao", value: "Macao", short: "MO", flag: "🇲🇴" }],
	["Mexico", { label: "Mexico", value: "Mexico", short: "MX", flag: "🇲🇽" }],
	["Malaysia", { label: "Malaysia", value: "Malaysia", short: "MY", flag: "🇲🇾" }],
	["Nigeria", { label: "Nigeria", value: "Nigeria", short: "NG", flag: "🇳🇬" }],
	["Netherlands", { label: "Netherlands", value: "Netherlands", short: "NL", flag: "🇳🇱" }],
	["New Zealand", { label: "New Zealand", value: "New Zealand", short: "NZ", flag: "🇳🇿" }],
	["Tahiti", { label: "Tahiti (French Polynesia)", value: "Tahiti", short: "PF", flag: "🇵🇫" }],
	["Philippines", { label: "Philippines", value: "Philippines", short: "PH", flag: "🇵🇭" }],
	["Pakistan", { label: "Pakistan", value: "Pakistan", short: "PK", flag: "🇵🇰" }],
	["Poland", { label: "Poland", value: "Poland", short: "PL", flag: "🇵🇱" }],
	["Portugal", { label: "Portugal", value: "Portugal", short: "PT", flag: "🇵🇹" }],
	["South Africa", { label: "South Africa", value: "South Africa", short: "RSA", flag: "🇿🇦" }],
	["Singapore", { label: "Singapore", value: "Singapore", short: "SG", flag: "🇸🇬" }],
	["Spain", { label: "Spain", value: "Spain", short: "SP", flag: "🇪🇸" }],
	["Chad", { label: "Chad", value: "Chad", short: "TD", flag: "🇹🇩" }],
	["Thailand", { label: "Thailand", value: "Thailand", short: "TH", flag: "🇹🇭" }],
	["East Timor", { label: "East Timor (Timor-Leste)", value: "East Timor", short: "TL", flag: "🇹🇱" }],
	["Turkey", { label: "Turkey", value: "Turkey", short: "TR", flag: "🇹🇷" }],
	["Taiwan", { label: "Taiwan", value: "Taiwan", short: "TW", flag: "🇹🇼" }],
	["UAE", { label: "United Arab Emirates", value: "UAE", short: "UAE", flag: "🇦🇪" }],
	["United Kingdom", { label: "United Kingdom", value: "United Kingdom", short: "UK", flag: "🇬🇧" }],
	["United States", { label: "United States", value: "United States", short: "USA", flag: "🇺🇸" }],
	["Vietnam", { label: "Vietnam", value: "Vietnam", short: "VN", flag: "🇻🇳" }],
]);

// Convert to array for components that need array format (like Select options)
export const countryOptions = Array.from(countryOptionsMap.values());

// Helper function to get country flag emoji - O(1) lookup
export function getCountryFlag(countryName: string): string {
	const country = countryOptionsMap.get(countryName);
	return country ? country.flag : "🏳️";
}

// Map non-standard short codes to ISO 3166-1 alpha-2 for SVG flag icons
const SHORT_TO_ISO: Record<string, string> = {
	CBDA: "KH",
	GCC: "",
	KAZ: "KZ",
	KSA: "SA",
	RSA: "ZA",
	SP: "ES",
	UAE: "AE",
	UK: "GB",
	USA: "US",
};

/**
 * Returns a country flag SVG URL (same logic as VettingChain).
 * If no valid ISO alpha-2 can be derived, returns empty string.
 */
export function getCountryFlagUrl(countryName: string): string {
	const short = getCountryShort(countryName);
	const iso = SHORT_TO_ISO[short] ?? short;
	if (!iso || iso.length !== 2) return "";
	return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${iso}.svg`;
}

// Helper function to get country short name
export function getCountryShort(countryName: string): string {
	const country = countryOptionsMap.get(countryName);
	return country ? country.short : countryName;
}

// Helper function to get full country data
export function getCountryData(countryName: string): CountryOption | undefined {
	return countryOptionsMap.get(countryName);
}

/**
 * Split text into lines using CRLF or LF. Returns [''] for empty/undefined input.
 */
export function splitLines(text?: string | null): string[] {
	if (text === null || text === undefined) return [""];
	return String(text).split(/\r?\n/);
}

/**
 * Render text as split lines with <br /> separators.
 */
export function renderSplitLines(text?: string | null): React.ReactNode[] {
	return splitLines(text).map((line, index, lines) =>
		React.createElement(
			React.Fragment,
			{ key: `line-${index}` },
			line,
			index < lines.length - 1 ? React.createElement("br", { key: `br-${index}` }) : null,
		),
	);
}

/**
 * Formats a date in the app's resource/comment display style.
 * Example: 1:05 PM - 27 April 2026
 */
export const formatDisplayDateTime = (dateInput?: string | number | Date | null): string => {
	if (!dateInput) return new Date().toLocaleString();

	const date = new Date(dateInput);
	if (Number.isNaN(date.getTime())) return new Date().toLocaleString();

	const day = date.getDate();
	const monthNames = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];
	const monthName = monthNames[date.getMonth()];
	const year = date.getFullYear();
	const hours = date.getHours();
	const minutes = date.getMinutes().toString().padStart(2, "0");
	const ampm = hours >= 12 ? "PM" : "AM";
	const displayHours = hours % 12 || 12;

	return `${displayHours}:${minutes} ${ampm} - ${day} ${monthName} ${year}`;
};

export const formatTimecode = (time: number) => {
	const safeTime = Math.max(0, time);
	const totalMilliseconds = Math.round(safeTime * 1000);
	const hours = Math.floor(totalMilliseconds / 3600000);
	const minutes = Math.floor((totalMilliseconds % 3600000) / 60000);
	const seconds = Math.floor((totalMilliseconds % 60000) / 1000);
	const milliseconds = totalMilliseconds % 1000;

	return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
		.toString()
		.padStart(2, "0")};${Math.floor(milliseconds / 10)
		.toString()
		.padStart(2, "0")}`;
};

/**
 * Formats a number as a percentage string with the specified decimal places (default 2).
 * Safe to pass numbers, strings, or null/undefined.
 * @example
 * formatPercentage(0.458) // "0.46%"
 * formatPercentage(45.8) // "45.80%"
 * formatPercentage(45.8, 1) // "45.8%"
 */
export const formatPercentage = (value: number | string | null | undefined, decimals: number = 2): string => {
	if (value === null || value === undefined || value === "") {
		return "0%";
	}

	const num = Number(value);
	if (Number.isNaN(num)) {
		return "0%";
	}

	return num.toFixed(decimals) + "%";
};

export const getSafeUrl = (url: string) => {
	try {
		const parsed = new URL(url);

		if (parsed.protocol === "http:" || parsed.protocol === "https:") {
			return parsed.toString();
		}

		return "#";
	} catch {
		return "#";
	}
};
